"use client";

import React from "react";
import Hero from "@/components/tools/Hero";
import FAQ from "@/components/tools/FAQ";
import RelatedTopics from "@/components/tools/RelatedTopics";
import IntroSection from "@/components/services/dental-sealants/IntroSection";
import WhatAreDentalSealants from "@/components/services/dental-sealants/WhatAreDentalSealants";
import HistoricalContext from "@/components/services/dental-sealants/HistoricalContext";
import IndustryDefinitions from "@/components/services/dental-sealants/IndustryDefinitions";
import WhyChooseSealant from "@/components/services/dental-sealants/WhyChooseSealant";
import SealantApplicationProcess from "@/components/services/dental-sealants/SealantApplicationProcess";
import SealantMaintenance from "@/components/services/dental-sealants/SealantMaintenance";

const dentalSealantFAQs = [
  {
    question: "What are dental sealants?",
    answer:
      "Dental sealants are thin, protective coatings applied to the chewing surfaces of your back teeth – typically molars and premolars. These teeth have deep grooves and pits that trap food particles and bacteria, making them hard to clean with a toothbrush. Sealants act like raincoats for your teeth, filling crevices to create a smooth, easy-to-clean surface, reducing cavity risk and maintaining oral health.",
  },
  {
    question: "How do dental sealants work?",
    answer:
      "Dental sealants bond directly with tooth enamel, forming a strong, protective shield that prevents harmful substances from damaging the enamel. They act as a first line of defense against decay, keeping teeth healthy. The application is quick and painless: we clean and dry the tooth, apply an etchant solution to aid bonding, paint on a liquid sealant, harden it with a blue light, and polish the teeth.",
  },
  {
    question: "What are the advantages of dental sealants?",
    answer:
      "Dental sealants prevent decay before it starts, preserving natural tooth strength. They can reverse early decay by allowing saliva to remineralize enamel. Sealants are less invasive than fillings or root canals, requiring no drilling or tooth material removal.",
  },
  {
    question: "When is the best time to get dental sealants?",
    answer:
      "The ideal time is when a child's permanent molars erupt, usually between ages 6 and 12. Adults at higher risk for decay or with deep grooves can also benefit. It's never too late to get sealants! Contact us to schedule an appointment, and our dentists will help you achieve a lasting, healthy smile.",
  },
  {
    question: "How long do dental sealants last?",
    answer:
      "Dental sealants can last several years, depending on oral hygiene, diet, and dental health. With proper care and regular check-ups, sealants remain effective. During routine visits, our dentists check sealants and touch them up if needed to maintain protection against decay.",
  },
  {
    question: "Can dental sealants help reverse tooth decay?",
    answer:
      "In early stages, like white spot lesions, sealants can prevent further decay by blocking bacteria and allowing saliva to remineralize enamel, potentially reversing damage. If decay progresses beyond the enamel, sealants alone can't reverse it, and treatments like fillings or root canals may be needed. Our dentists will assess and recommend the best treatment.",
  },
  {
    question: "Do dental sealants require special care?",
    answer:
      "Dental sealants need no special care beyond good oral hygiene. Brush twice daily with fluoride toothpaste and floss daily to remove plaque and debris. Avoid hard or sticky foods right after application to prevent damage to the sealant material, especially in the days following your appointment.",
  },
  {
    question: "What happens if a dental sealant falls off?",
    answer:
      "If a sealant falls off, have it replaced promptly to protect the tooth from decay. Our dentists can reapply the sealant. During routine check-ups, we examine sealants and touch them up if needed. If a sealant falls off between visits, contact us to schedule an appointment.",
  },
  {
    question: "How can I tell if I need dental sealants?",
    answer:
      "Our dentists can determine if sealants are right for you during a check-up by examining your teeth for deep grooves, pits, or early decay. Even with excellent hygiene, sealants are an effective preventive measure. Ask our team about sealants at your next visit to maintain a healthy smile.",
  },
  {
    question:
      "What role do dental education programs play in promoting oral health?",
    answer:
      "Dental education programs promote oral health by offering sealant treatments in preventive care initiatives, often providing free or low-cost services to underserved communities. They reduce decay incidence, teach proper hygiene, and emphasize regular check-ups, fostering lasting oral health improvements.",
  },
];

const DentalSealants: React.FC = () => {
  return (
    <>
      <Hero
        imageSrc="/images/smiling-woman-during-dental-cleaning-procedure-as-dentist-examines-her-teeth-in-a-modern-dental-office-setting.webp"
        imageAlt="Dentist applying dental sealants in a modern dental office"
        title="Protect Your Smile with Dental Sealants in Astoria"
        subtitle="Smile4Me Dental Care offers expert sealant treatments to prevent cavities and keep your teeth healthy."
        ctaText="Book Now"
        ctaLink="/contact"
      />
      <main className="flex-1 py-8 bg-gradient-to-b from-white to-gray-50">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">
            <IntroSection />
            <WhatAreDentalSealants />
            <HistoricalContext />
            <IndustryDefinitions />
            <WhyChooseSealant />
            <SealantApplicationProcess />
            <SealantMaintenance />
            <FAQ
              title="Frequently Asked Questions"
              faqs={dentalSealantFAQs}
            />
          </article>
          <RelatedTopics
            topics={[
              {
                label: "Dental Cleanings",
                url: "/services/dental-cleanings",
              },
              {
                label: "Fluoride Treatment",
                url: "/services/fluoride-treatment",
              },
              { label: "Teeth Whitening", url: "/services/teeth-whitening" },
              { label: "Oral Exams", url: "/services/oral-exams" },
              {
                label: "Pediatric Dentistry",
                url: "/services/pediatric-dentistry",
              },
            ]}
          />
        </div>
      </main>
    </>
  );
};

export default DentalSealants;